import React, { useCallback, useState } from 'react';
import * as Sentry from '@sentry/react';
import styled from 'styled-components/macro';
import { useForm, Controller } from 'react-hook-form';
import { useApolloClient } from '@apollo/client';
import { email as emailAddressRegex, phone as phoneNumberRegex } from 'shared/utils/validation';
import { DeviceSizes } from 'shared/enums/DeviceSizes';
import { PatientSex } from 'shared/enums/PatientSex';
import { PatientWeightUnit } from 'shared/enums/PatientWeightUnit';
import { AvatarSearchIcon } from 'assets/icons';
import TextInput, { LabelText } from 'shared/components/TextInput';
import SelectInput from 'shared/components/SelectInput';
import BirthDateSelect from 'shared/components/BirthDateSelect';
import useClinicUser from 'shared/hooks/useClinicUser';
import {
  useCreateOneClinicPetParentMutation,
  ClinicPetParentCreateInput,
  useCreateOneClinicPetMutation,
  ClinicPetCreateInput,
  ClinicPetParentWhereInput,
  CheckForExistingClinicPetParentQueryVariables,
  CheckForExistingClinicPetParentQuery,
  ExistingClinicPetParentMatchFragment,
  SpeciesType,
  SidePanelPetParentFragment,
} from 'shared/types/graphql';
import { CHECK_EXISTING_CLINIC_PET_PARENT } from 'shared/queries/checkExistingClinicPetParent';
import TruncatedPetList from 'shared/components/TruncatedPetList';
import Tooltip from 'shared/components/Tooltip';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import PetParentAvatar from 'shared/components/Avatars/PetParentAvatar';
import PetAvatar from '../../../Avatars/PetAvatar';
import { useSidePanelSearch } from '../../components/SearchPanel/state/providers/SearchPanelProvider';
import { getClinicPetParentDisplayName } from 'shared/utils';
import ClientSearchResult from 'shared/components/SidePanel/shared/components/ClientSearchResult';
import { Mode } from 'shared/components/SidePanel/components/SearchPanel/shared/types/enum/Mode';
import { useValidation } from '../../../../hooks/useValidation';
import { Text, VStack, HStack, Icon, Button, Heading, Link, Divider, useToast } from '@televet/kibble-ui';

const petSpeciesOptions = [
  { label: '', value: undefined },
  ...Object.entries(SpeciesType).map(([key, value]) => ({ label: value, value: key })),
];

const patientSexOptions = [
  {
    label: '',
    value: undefined,
  },
  ...Object.entries(PatientSex).map(([key, value]) => ({ label: value, value: key })),
];

const patientWeightUnitOptions = [
  {
    label: 'lb',
    value: PatientWeightUnit.Pound,
  },
  {
    label: 'kg',
    value: PatientWeightUnit.Kilogram,
  },
];

interface IAddClientPatientForm {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  patientName?: string;
  patientDateOfBirth?: Date;
  petSpecies?: SpeciesType;
  petSpeciesOther?: string;
  patientBreed?: string;
  patientSex?: PatientSex;
  patientWeight?: number;
  patientWeightUnit?: PatientWeightUnit;
}

interface IAddClientPatientFormProps {
  onBackButtonClick: () => void;
  goToPetParent: (clinicPetParentId: string) => Promise<void>;
  goToPet: (clinicPetId: string, fromNetwork: boolean) => Promise<void>;
  addUserToChannel?: (petParentId: string) => Promise<void>;
  resultMessageText?: string;
  onSelectExistingPetParent?: (clinicPetParent: ExistingClinicPetParentMatchFragment) => void;
  searchPanelMode?: Mode;
  onAddClientClickOverride?: {
    callback: (client: SidePanelPetParentFragment) => void;
  };
  closePanel?: () => void;
}

const AddClientPatientForm = ({
  onBackButtonClick,
  goToPetParent,
  goToPet,
  addUserToChannel,
  resultMessageText = `We have a match! Choose an existing client below to view their profile or provide another email
  address to add a new client.`,
  onSelectExistingPetParent,
  searchPanelMode = Mode.Default,
  onAddClientClickOverride,
  closePanel,
}: IAddClientPatientFormProps): JSX.Element | null => {
  const { validatePhoneNumber } = useValidation();
  const { selectedClient, selectedPatient, conversationId, viewSearch, setSearchPanelMode } = useSidePanelSearch();
  const isAddingPatient = !!selectedClient;
  const { currentClinicId } = useClinicUser();
  const toast = useToast();
  const apolloClient = useApolloClient();

  const [createClinicPetParent, { loading: isCreatingClinicPetParent }] = useCreateOneClinicPetParentMutation();

  const [createClinicPet, { loading: isCreatingClinicPet }] = useCreateOneClinicPetMutation();

  const isSubmittingForm = isCreatingClinicPetParent || isCreatingClinicPet;

  const { handleSubmit, register, getValues, setValue, triggerValidation, watch, control, errors, formState } =
    useForm<IAddClientPatientForm>({
      mode: 'onBlur',
      reValidateMode: 'onBlur',
      validateCriteriaMode: 'all',
    });

  const handleAddClientSubmit = async ({
    firstName,
    lastName,
    email,
    phoneNumber,
    patientName,
    patientDateOfBirth,
    petSpecies,
    petSpeciesOther,
    patientBreed,
    patientSex,
    patientWeight,
    patientWeightUnit,
  }: IAddClientPatientForm): Promise<void> => {
    if (!currentClinicId || isCreatingClinicPetParent) return;
    try {
      const data: ClinicPetParentCreateInput = {
        clinic: {
          connect: { id: currentClinicId },
        },
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        phoneNumbers: phoneNumber
          ? { create: [{ number: phoneNumber.replace(/\D/g, ''), isPrimary: true, isMobile: true }] }
          : undefined,
      };

      if (selectedPatient) {
        data.pets = {
          connect: [
            {
              id: selectedPatient.id,
            },
          ],
        };
      } else if (patientName || patientDateOfBirth || petSpecies || petSpecies || patientBreed || patientWeight) {
        data.pets = {
          create: [
            {
              clinic: { connect: { id: currentClinicId } },
              name: patientName?.trim() || 'Unknown',
              dateOfBirth: patientDateOfBirth || null,
              species: (petSpecies === SpeciesType.Other ? petSpeciesOther || SpeciesType.Other : petSpecies) || null,
              /**
               * We include `speciesType` here because Prisma middleware/client extensions don't run on
               * nested operations like this one. We rely on the middleware to populate `speciesType` for
               * all other top-level ClinicPet create or update operations.
               *
               * See middleware: https://github.com/TeleVet/db-sdk-prisma2/blob/master/src/validations/models/ClinicPet.ts#L14
               */
              speciesType: petSpecies || undefined, //
              breed: patientBreed || null,
              gender: patientSex || null,
              weight: Number(patientWeight) || null,
              weightUnit: patientWeight ? patientWeightUnit : null,
            },
          ],
        };
      }
      const result = await createClinicPetParent({ variables: { data } });
      const clinicPetParent = result.data?.createOneClinicPetParent;
      if (!clinicPetParent) throw new Error('No createClinicPetParent returned from mutation');

      if (addUserToChannel && !!conversationId) {
        if (onAddClientClickOverride) {
          onAddClientClickOverride.callback(clinicPetParent);
          return closePanel?.();
        } else {
          await addUserToChannel(clinicPetParent.id);
          setSearchPanelMode(Mode.Default);

          toast({
            status: 'success',
            title: `${getClinicPetParentDisplayName(clinicPetParent)} was successfully added to the conversation`,
          });
          toast({
            status: 'success',
            title: `Success! You added  ${getClinicPetParentDisplayName(clinicPetParent)} as a new contact`,
          });
        }
      }
      if (selectedPatient) {
        goToPet(selectedPatient.id, true);
      } else {
        goToPetParent(clinicPetParent.id || '');
      }
      if (!conversationId) {
        toast({
          status: 'success',
          title: `Success! You added  ${getClinicPetParentDisplayName(clinicPetParent)} as a new client`,
        });
      }
    } catch (error) {
      Sentry.captureException(error);
      console.error(error);
      toast({
        status: 'error',
        title: 'Whoops! There was a problem adding the client. Please try again.',
      });
    }
  };

  const handleAddPatientSubmit = async ({
    patientName,
    patientDateOfBirth,
    petSpecies,
    petSpeciesOther,
    patientBreed,
    patientSex,
    patientWeight,
    patientWeightUnit,
  }: IAddClientPatientForm): Promise<void> => {
    if (!currentClinicId || isCreatingClinicPetParent || !selectedClient) return;
    try {
      const data: ClinicPetCreateInput = {
        clinic: {
          connect: { id: currentClinicId },
        },
        petParents: { connect: [{ id: selectedClient.id }] },
        name: patientName || 'Unknown',
        dateOfBirth: patientDateOfBirth || null,
        species: (petSpecies === SpeciesType.Other ? petSpeciesOther || SpeciesType.Other : petSpecies) || null,
        breed: patientBreed || null,
        gender: patientSex || null,
        weight: Number(patientWeight) || null,
        weightUnit: patientWeight ? patientWeightUnit : null,
      };
      const result = await createClinicPet({ variables: { data } });
      const clinicPet = result.data?.createOneClinicPet;
      if (!clinicPet) throw new Error('No createClinicPet returned from mutation');
      goToPetParent(selectedClient?.id || '');
      toast({
        status: 'success',
        title: `Success! You added ${clinicPet.name} as a patient.`,
      });
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);
      toast({
        status: 'error',
        title: 'Whoops! There was a problem adding the patient. Please try again.',
      });
    }
  };

  const [clinicPetParentEmailMatches, setClinicPetParentEmailMatches] = useState<
    ExistingClinicPetParentMatchFragment[]
  >([]);
  const [clinicPetParentPhoneMatches, setClinicPetParentPhoneMatches] = useState<
    ExistingClinicPetParentMatchFragment[]
  >([]);

  const isExistingClinicPetParent = async (criteria: { email?: string; phoneNumber?: string }): Promise<boolean> => {
    const where = {} as ClinicPetParentWhereInput;
    if (criteria.email) {
      where.email = { equals: criteria.email };
    }
    if (criteria.phoneNumber) {
      where.phoneNumbers = {
        some: {
          number: { equals: criteria.phoneNumber },
          isPrimary: { equals: true },
          isDeleted: { equals: false },
        },
      };
    }

    if (!currentClinicId) {
      throw new Error('No clinic ID was provided!');
    }

    const result = await apolloClient.query<
      CheckForExistingClinicPetParentQuery,
      CheckForExistingClinicPetParentQueryVariables
    >({
      query: CHECK_EXISTING_CLINIC_PET_PARENT,
      variables: {
        where: {
          clinic: {
            id: {
              equals: currentClinicId,
            },
          },
          ...where,
        },
      },
      fetchPolicy: GraphQLFetchPolicies.NetworkOnly,
    });
    const clinicPetParents = result?.data?.findManyClinicPetParent;
    const exists = !!clinicPetParents?.length;
    if (exists && criteria.email) setClinicPetParentEmailMatches(clinicPetParents || []);
    if (exists && criteria.phoneNumber) setClinicPetParentPhoneMatches(clinicPetParents || []);
    return exists;
  };

  const handlePhoneNumberBlur = async (event: React.FocusEvent<HTMLInputElement>): Promise<void> => {
    if (!event.target.value) return;
    const phone = event.target.value;
    if (!!phone) {
      setValue('phoneNumber', phone.replace(phoneNumberRegex, '$2-$3-$4'));
    }
  };

  const handleClickExistingParent = useCallback(
    (clinicPetParent: ExistingClinicPetParentMatchFragment): void => {
      if (typeof onSelectExistingPetParent === 'function') {
        onSelectExistingPetParent(clinicPetParent);
      } else {
        goToPetParent(clinicPetParent.id);
      }
    },
    [onSelectExistingPetParent, goToPetParent],
  );

  watch('petSpecies'); // Re-render to ensure text input appears when "other" is selected

  return (
    <AddClientPatientFormContainer>
      <HStack className="AddClientPatientForm__Header" borderBottomWidth="1px" borderBottomColor="border.default" p={2}>
        <Tooltip content={selectedClient || selectedPatient ? 'Back to Profile' : 'Back to Search'}>
          <Button onClick={onBackButtonClick} iconName="chevronLeft" variant="ghostNeutral" />
        </Tooltip>
        <Text size="lg" fontWeight="bold">
          {isAddingPatient ? 'Add new patient' : !!conversationId ? 'Add new contact' : 'Add new client'}
        </Text>
      </HStack>

      <StyledForm onSubmit={handleSubmit(isAddingPatient ? handleAddPatientSubmit : handleAddClientSubmit)}>
        <Body>
          {isAddingPatient && selectedClient ? (
            <VStack w="100%" align="flex-start" px={4}>
              <Heading size="sm">Selected Client</Heading>
              <HStack
                className="SelectedClient__Container"
                w="100%"
                padding={2}
                boxShadow="md"
                borderRadius="lg"
                bg="background.subtle"
              >
                <PetParentAvatar clinicPetParent={selectedClient} size="lg" />
                <EntityTextContainer>
                  <EntityName>{getClinicPetParentDisplayName(selectedClient)}</EntityName>
                </EntityTextContainer>
              </HStack>
            </VStack>
          ) : (
            <>
              <Row>
                <TextInput
                  name="firstName"
                  label={!!conversationId ? 'Contact First Name' : 'Client First Name'}
                  validation={{ required: 'Please enter a first name' }}
                  errors={errors}
                  autoFocus={true}
                  register={register}
                />
              </Row>
              <Row>
                <TextInput
                  name="lastName"
                  label={!!conversationId ? 'Contact Last Name' : 'Client Last Name'}
                  validation={{ required: 'Please enter a last name' }}
                  errors={errors}
                  register={register}
                />
              </Row>
              <Row>
                <TextInput
                  name="phoneNumber"
                  label={!!conversationId ? 'Contact Phone Number' : 'Client Phone Number'}
                  validation={{
                    pattern: {
                      value: phoneNumberRegex,
                      message: 'Please provide a valid US phone number',
                    },
                    validate: {
                      isProvided: (value): boolean | string => {
                        if (!!value && !getValues('email')) triggerValidation('email');
                        return (
                          !!value || !!getValues('email') || 'Please provide a client email address or phone number'
                        );
                      },
                      isUnique: async (value): Promise<boolean | string> => {
                        // Only make request to check for uniqueness if phone number is valid
                        if (!phoneNumberRegex.test(value)) return true;
                        return !(await isExistingClinicPetParent({ phoneNumber: value.replace(/\D/g, '') })) || '';
                      },
                      isReserved: async (value): Promise<string | true | undefined> => {
                        const { isValid, message } = await validatePhoneNumber(value);
                        return !value || isValid || message;
                      },
                    },
                  }}
                  errors={errors}
                  register={register}
                  onChange={(): void => {
                    if (clinicPetParentPhoneMatches.length) setClinicPetParentPhoneMatches([]);
                  }}
                  onBlur={handlePhoneNumberBlur}
                />
              </Row>
              {!!clinicPetParentPhoneMatches.length && (
                <InfoContainer>
                  <ExistingClientsDescription>
                    <AvatarSearchIcon />
                    {resultMessageText}
                  </ExistingClientsDescription>
                  {clinicPetParentPhoneMatches.map((clinicPetParent) => {
                    const { id, firstName, lastName, pets, ...rest } = clinicPetParent;
                    return (
                      <ExistingClient
                        onClick={(): void => handleClickExistingParent({ id, firstName, lastName, pets, ...rest })}
                        key={id}
                      >
                        <span>{getClinicPetParentDisplayName(clinicPetParent)}</span>
                        {!!pets?.length && <TruncatedPetList clinicPets={pets} />}
                      </ExistingClient>
                    );
                  })}
                </InfoContainer>
              )}
              <Row>
                <TextInput
                  name="email"
                  label={!!conversationId ? 'Contact Email Address' : 'Client Email Address'}
                  validation={{
                    validate: {
                      isProvided: (value): boolean | string => {
                        if (!!value && !getValues('phoneNumber')) triggerValidation('phoneNumber');
                        return (
                          !!value ||
                          !!getValues('phoneNumber') ||
                          'Please provide a client email address or phone number'
                        );
                      },
                      isUnique: async (value): Promise<boolean | string> => {
                        if (!emailAddressRegex.test(value)) return true;
                        return !(await isExistingClinicPetParent({ email: value })) || '';
                      },
                    },
                    pattern: {
                      value: emailAddressRegex,
                      message: 'Please provide a valid email address',
                    },
                  }}
                  errors={errors}
                  register={register}
                  onChange={(): void => {
                    if (clinicPetParentEmailMatches.length) setClinicPetParentEmailMatches([]);
                  }}
                />
              </Row>
              {!!clinicPetParentEmailMatches.length && (
                <InfoContainer>
                  <ClientSearchResult
                    icon={<Icon name="team" />}
                    messageText={resultMessageText}
                    backgroundColor="background.default"
                  >
                    {clinicPetParentEmailMatches.map((clinicPetParent) => {
                      const { id, firstName, lastName, pets, ...rest } = clinicPetParent;
                      return (
                        <ExistingClient
                          onClick={(): void => handleClickExistingParent({ id, firstName, lastName, pets, ...rest })}
                          key={id}
                        >
                          <span>{getClinicPetParentDisplayName(clinicPetParent)}</span>
                          {!!pets?.length && <TruncatedPetList clinicPets={pets} />}
                        </ExistingClient>
                      );
                    })}
                  </ClientSearchResult>
                </InfoContainer>
              )}
            </>
          )}

          <Divider mb={4} />
          {!isAddingPatient && (
            <HStack
              mx={4}
              mb={4}
              onMouseDown={(e): void => {
                e.preventDefault();
              }}
            >
              <Icon name="magnifyingGlass" />
              <Link
                ml={2}
                size="sm"
                onClick={(): void => {
                  if (!conversationId) {
                    setSearchPanelMode(Mode.LinkToPet);
                  }
                  viewSearch();
                }}
                variant="subtle"
              >
                Search for an existing client instead
              </Link>
            </HStack>
          )}
          <Divider mb={4} />

          {((): JSX.Element | null => {
            /**
             * Show selected patient
             */
            if (selectedPatient && searchPanelMode === Mode.Default) {
              return (
                <VStack w="100%" align="flex-start" px={4}>
                  <Heading size="sm">Selected Patient</Heading>
                  <HStack
                    className="SelectedPatient__Container"
                    w="100%"
                    p={3}
                    boxShadow="md"
                    borderRadius="lg"
                    bg="background.subtle"
                  >
                    <PetAvatar
                      speciesType={selectedPatient.speciesType}
                      isDeceased={selectedPatient.computedIsDeceased}
                      isActive={selectedPatient.computedIsActive}
                      photoUrl={selectedPatient.photoUrl}
                      name={selectedPatient.name}
                    />
                    <VStack spacing={0} align="flex-start">
                      <Text fontWeight="bold">{selectedPatient.name}</Text>
                      <Text size="sm">{selectedPatient.breed || 'Breed not recorded'}</Text>
                      <Text size="sm">{selectedPatient.gender || 'Sex not recorded'}</Text>
                    </VStack>
                  </HStack>
                </VStack>
              );
            }

            /**
             * Add new patient
             */
            if (!selectedPatient && searchPanelMode === Mode.Default) {
              return (
                <>
                  <Row>
                    <TextInput
                      name="patientName"
                      label="Patient Name"
                      validation={{
                        required: isAddingPatient && 'Please provide a patient name',
                      }}
                      errors={errors}
                      autoFocus={isAddingPatient}
                      register={register}
                    />
                  </Row>
                  <Row>
                    <Controller
                      name="patientDateOfBirth"
                      label="Patient Date of Birth"
                      errors={errors}
                      control={control}
                      as={BirthDateSelect}
                    />
                  </Row>
                  <Row collapseSpacing={getValues('petSpecies') === SpeciesType.Other}>
                    <SelectInput
                      name="petSpecies"
                      label="Patient Species"
                      options={petSpeciesOptions}
                      register={register}
                    />
                  </Row>
                  {getValues('petSpecies') === SpeciesType.Other && (
                    <Row>
                      <TextInput name="petSpeciesOther" label="" placeholder="Please specify" register={register} />
                    </Row>
                  )}
                  <Row>
                    <TextInput name="patientBreed" label="Patient Breed" register={register} />
                  </Row>
                  <Row>
                    <SelectInput
                      name="patientSex"
                      label="Patient Sex"
                      options={patientSexOptions}
                      register={register}
                    />
                  </Row>
                  <Row>
                    <label>
                      <LabelText>Patient Weight</LabelText>
                      <PatientWeightContainer>
                        <TextInput
                          name="patientWeight"
                          label=""
                          validation={{
                            validate: (value): boolean | string => !isNaN(value) || 'Please provide a number',
                          }}
                          errors={errors}
                          register={register}
                        />
                        <SelectInput
                          name="patientWeightUnit"
                          label=""
                          options={patientWeightUnitOptions}
                          errors={errors}
                          register={register}
                        />
                      </PatientWeightContainer>
                    </label>
                  </Row>
                </>
              );
            }

            /**
             * searchPanelMode === Mode.LinkToChannel
             * So we don't show the patient form
             */
            return null;
          })()}
        </Body>
        <Footer>
          <Button
            isFullWidth
            isLoading={isSubmittingForm}
            isDisabled={!formState.isValid || isCreatingClinicPetParent}
            type="submit"
          >
            {isAddingPatient ? 'Add Patient' : !!conversationId ? 'Add Contact' : 'Add Client'}
          </Button>
        </Footer>
      </StyledForm>
    </AddClientPatientFormContainer>
  );
};

const AddClientPatientFormContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
`;

const StyledForm = styled.form`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  overflow: hidden;
`;

const Body = styled.div`
  flex: 1 1 auto;
  padding: 20px 0 5px;
  overflow-y: scroll;
`;

interface IRowProps {
  collapseSpacing?: boolean;
}

const Row = styled.div<IRowProps>`
  margin-bottom: ${({ collapseSpacing }): string => (collapseSpacing ? '10px' : '20px')};
  padding: 0 20px;
`;

const InfoContainer = styled.div`
  border-radius: 11px;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.08);
  overflow: hidden;
  margin: -15px 20px 20px;

  font-size: 14px;
`;

const ExistingClientsDescription = styled.div`
  display: flex;
  align-items: flex-start;
  color: var(--chakra-colors-text-default);
  padding: 12px 12px 8px;
  line-height: 1.4;

  & svg {
    flex: 0 0 auto;
    margin-right: 12px;
  }
`;

export const ExistingClient = styled.div`
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 20px;
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    background: #eee;
  }

  &:last-of-type {
    margin-bottom: 12px;
  }

  & > *:first-child {
    margin-right: 8px;
  }
`;

const EntityTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 12px;
`;

const EntityName = styled.div`
  color: #4e526f;
  font-weight: 600;
  font-size: 16px;
`;

const PatientWeightContainer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;

  & > *:first-child {
    margin-right: 10px;
    flex: 1 1 auto;
  }

  & > *:last-child {
    flex: 0 0 auto;
  }
`;

const Footer = styled.div`
  flex: 0 0 auto;
  padding: 20px;

  border-top: 1px solid #ddd;
  @media (${DeviceSizes.MobileMaxWidth}) {
    padding-bottom: 70px;
  }
`;

export default AddClientPatientForm;
